import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Tag, 
  Modal, 
  Select, 
  Alert, 
  Typography, 
  Space,
  Descriptions,
  message 
} from 'antd';
import { EyeOutlined, CheckOutlined } from '@ant-design/icons';
import './FlaggedCategorization.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Mock data for development - will be replaced with real API calls
const mockFlaggedProducts = [
  {
    id: '1',
    productName: 'Premium Cigarettes Pack',
    productDescription: 'High-quality tobacco cigarettes, 20 pack',
    merchantName: 'Tobacco Plus Store',
    aiSuggestion: 'Tobacco Products > Cigarettes',
    confidence: 0.65,
    flaggedReason: 'Low confidence score',
    dateAdded: '2024-01-15',
    status: 'pending'
  },
  {
    id: '2',
    productName: 'Craft Beer 6-Pack',
    productDescription: 'Local brewery craft beer assortment',
    merchantName: 'Beverage Depot',
    aiSuggestion: 'Alcoholic Beverages > Beer',
    confidence: 0.72,
    flaggedReason: 'Merchant disagreed with categorization',
    dateAdded: '2024-01-14',
    status: 'pending'
  },
];

// Mock tax categories - will be fetched from API
const mockTaxCategories = [
  { id: 'tobacco-cigarettes', name: 'Tobacco Products > Cigarettes', path: 'tobacco.cigarettes' },
  { id: 'tobacco-cigars', name: 'Tobacco Products > Cigars', path: 'tobacco.cigars' },
  { id: 'alcohol-beer', name: 'Alcoholic Beverages > Beer', path: 'alcohol.beer' },
  { id: 'alcohol-wine', name: 'Alcoholic Beverages > Wine', path: 'alcohol.wine' },
  { id: 'general-retail', name: 'General Retail', path: 'general.retail' },
];

function FlaggedCategorization() {
  const [flaggedProducts, setFlaggedProducts] = useState([]);
  const [taxCategories, setTaxCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFlaggedProducts();
    fetchTaxCategories();
  }, []);

  const fetchFlaggedProducts = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/admin/flagged-products');
      // const data = await response.json();
      
      // Using mock data for now
      setTimeout(() => {
        setFlaggedProducts(mockFlaggedProducts);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error fetching flagged products:', error);
      message.error('Failed to fetch flagged products');
      setLoading(false);
    }
  };

  const fetchTaxCategories = async () => {
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/admin/tax-categories');
      // const data = await response.json();
      
      // Using mock data for now
      setTaxCategories(mockTaxCategories);
    } catch (error) {
      console.error('Error fetching tax categories:', error);
      message.error('Failed to fetch tax categories');
    }
  };

  const handleReviewProduct = (product) => {
    setSelectedProduct(product);
    setSelectedCategory(null);
    setReviewModalVisible(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedCategory) {
      message.warning('Please select a tax category');
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Replace with actual API call
      // await fetch('/api/admin/categorization-decision', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     productId: selectedProduct.id,
      //     categoryId: selectedCategory,
      //     action: 'approve'
      //   })
      // });

      // Mock API call
      setTimeout(() => {
        message.success('Product categorization updated successfully');
        setFlaggedProducts(prev => 
          prev.filter(product => product.id !== selectedProduct.id)
        );
        setReviewModalVisible(false);
        setSelectedProduct(null);
        setSelectedCategory(null);
        setSubmitting(false);
      }, 1000);
    } catch (error) {
      console.error('Error submitting review:', error);
      message.error('Failed to submit review');
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.merchantName}
          </Text>
        </div>
      ),
    },
    {
      title: 'AI Suggestion',
      dataIndex: 'aiSuggestion',
      key: 'aiSuggestion',
      render: (text, record) => (
        <div>
          <Text>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Confidence: {Math.round(record.confidence * 100)}%
          </Text>
        </div>
      ),
    },
    {
      title: 'Flagged Reason',
      dataIndex: 'flaggedReason',
      key: 'flaggedReason',
      render: (text) => <Tag color="orange">{text}</Tag>,
    },
    {
      title: 'Date Added',
      dataIndex: 'dateAdded',
      key: 'dateAdded',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'pending' ? 'processing' : 'success'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleReviewProduct(record)}
          size="small"
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <div className="flagged-categorization">
      <div className="flagged-categorization-header">
        <Title level={2}>Flagged Product Categorization Review</Title>
        <Text type="secondary">
          Review products flagged for manual categorization verification
        </Text>
      </div>

      <Card className="flagged-categorization-card">
        <div className="table-header">
          <Space>
            <Text strong>Pending Reviews: {flaggedProducts.length}</Text>
            <Button 
              onClick={fetchFlaggedProducts} 
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={flaggedProducts}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          className="flagged-products-table"
        />
      </Card>

      <Modal
        title="Review Product Categorization"
        open={reviewModalVisible}
        onCancel={() => setReviewModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setReviewModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={handleSubmitReview}
            icon={<CheckOutlined />}
          >
            Approve Categorization
          </Button>,
        ]}
        width={800}
        className="review-modal"
      >
        {selectedProduct && (
          <div className="review-content">
            <Alert
              message="Review Required"
              description={`This product was flagged: ${selectedProduct.flaggedReason}`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Descriptions title="Product Details" bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Product Name" span={3}>
                {selectedProduct.productName}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={3}>
                {selectedProduct.productDescription}
              </Descriptions.Item>
              <Descriptions.Item label="Merchant" span={2}>
                {selectedProduct.merchantName}
              </Descriptions.Item>
              <Descriptions.Item label="Date Added">
                {selectedProduct.dateAdded}
              </Descriptions.Item>
            </Descriptions>

            <div className="ai-suggestion-section">
              <Title level={4}>AI Categorization Suggestion</Title>
              <div className="ai-suggestion-content">
                <Text strong>{selectedProduct.aiSuggestion}</Text>
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {Math.round(selectedProduct.confidence * 100)}% confidence
                </Tag>
              </div>
            </div>

            <div className="category-selection-section">
              <Title level={4}>Select Correct Tax Category</Title>
              <Select
                placeholder="Choose the correct tax category"
                style={{ width: '100%' }}
                value={selectedCategory}
                onChange={setSelectedCategory}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {taxCategories.map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default FlaggedCategorization;